import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { BASE_URL } from '../lib/Api';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('Confirming your email...');
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const hasCalledApi = useRef(false);

  useEffect(() => {
    if (!token || hasCalledApi.current) {
      if (!token) {
        setStatus('error');
        setMessage('Missing confirmation token.');
      }
      return;
    }

    const confirmEmail = async () => {
      hasCalledApi.current = true;

      try {
        const res = await fetch(`${BASE_URL}/authentication/confirm-email?token=${token}`);
        const data = await res.json();

        console.log('[ConfirmEmail] Response:', data);

        if (data.success) {
          setStatus('success');
          setMessage(data.message || 'Your email has been successfully verified.');
        } else {
          setStatus('error');
          setMessage(data.message || 'Invalid or expired confirmation token.');
        }
      } catch (err) {
        console.error('[ConfirmEmail] Error:', err);
        setStatus('error');
        setMessage('Something went wrong. Please try again later.');
      }
    };

    confirmEmail();
  }, [token, navigate]);

  return (
    <div className="flex min-h-screen w-full items-center justify-center animated-gradient-background p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex w-full justify-center">
          <img
            src="./images/Evolvv-main.png"
            alt="evolvv Logo"
            width={200}
            height={50}
            className="dark:invert"
          />
        </div>

        <Card className="w-full shadow-xl bg-card/90 backdrop-blur-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-card-foreground">
              Email Confirmation
            </CardTitle>
          </CardHeader>

          <CardContent className="flex flex-col items-center space-y-6 text-center py-8">
            {status === 'loading' && (
              <>
                <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />
                <p className="text-muted-foreground text-lg">{message}</p>
                <p className="text-sm text-muted-foreground">
                  Please wait while we verify your email address...
                </p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <CheckCircle className="text-green-500 h-20 w-20 animate-bounce" />
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-green-600 dark:text-green-400">
                    Email Verified Successfully!
                  </h3>
                  <p className="text-foreground font-semibold text-lg">{message}</p>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-xl border-2 border-green-200 dark:border-green-800 w-full">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="text-left space-y-2">
                      <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                        🎉 Your account is now active!
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-400">
                        You can now close this window and login to your account to start using Evolvv.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground pt-4">
                  Thank you for verifying your email address
                </div>
              </>
            )}
            
            {status === 'error' && (
              <>
                <XCircle className="text-red-500 h-20 w-20" />
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-red-600 dark:text-red-400">
                    Verification Failed
                  </h3>
                  <p className="text-destructive font-semibold text-lg">{message}</p>
                </div>

                <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl border-2 border-red-200 dark:border-red-800 w-full">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-left space-y-2">
                      <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                        What to do next:
                      </p>
                      <ul className="text-xs text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
                        <li>This link may have expired or already been used</li>
                        <li>Try signing up again with a new account</li>
                        <li>Contact support if the problem continues</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground pt-4">
                  You can close this window
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}